import { useState } from "react";
import {
    createTable,
    PaginationState,
    SearchState,
    SortingState,
    TableHeaderSpec,
} from "../../../../../components/table/CreateTable.tsx";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { toDecimalFromMoney } from "../../../../../lib/Money.ts";
import { cleanDocumentFileName } from "../../../../../lib/TFICLib.ts";
import type { OutstandingBillRow } from "./types/outstandingBill.ts";
import {
    daysUntilDueToColorClass,
    formatDueDateLabel,
    formatOpenDocumentsTitle,
} from "./utils/outstandingBillsFormat.ts";

interface Props {
    bills: OutstandingBillRow[];
    totalCount: number;
    isFiltered: boolean;
    onRowClick: (bill: OutstandingBillRow) => void;
    onReportDocumentIncorrect?: (bill: OutstandingBillRow) => void;
    emptyMessage: string;
}

const PAGE_SIZE = 25;

const DUE_DATE_FIELD = "normalized_due_date";
const REPORT_INCORRECT_LABEL = "Dit klopt niet";
const EMPTY_FIELD_VALUE = "";

const DEFAULT_SORTING_STATE: SortingState = { orderBy: DUE_DATE_FIELD, orderDescending: false };

const SEARCHABLE_FIELDS: Array<(row: OutstandingBillRow) => string | null> = [
    (row) => cleanDocumentFileName(row.file_name),
    (row) => row.relation,
];

export function OutstandingBillsTable({ bills, totalCount, isFiltered, onRowClick, onReportDocumentIncorrect, emptyMessage }: Props) {
    const tablePaginationState = useState<PaginationState<OutstandingBillRow>>();
    const tableSortingState = useState<SortingState>(DEFAULT_SORTING_STATE);

    const tableSpec: TableHeaderSpec<OutstandingBillRow> = {
        title: formatOpenDocumentsTitle(bills.length, totalCount, isFiltered),
        columns: [
            {
                name: "Bestandsnaam",
                field: "file_name",
                customRender: (row) => (
                    <span title={row.file_name}>
                        {cleanDocumentFileName(row.file_name)}
                    </span>
                ),
                sortValue: (row) => cleanDocumentFileName(row.file_name),
                style: { widthMultiplier: 2 },
            },
            {
                name: "Bedrag",
                field: "amount",
                customRender: (row) => (
                    <span>
                         <MoneyLabel value={toDecimalFromMoney(row.amount)}  showCurrency={true} currency={row.amount.currency}/>
                    </span>
                ),
                sortValue: (row) => row.amount.signed_amount,
                style: { widthMultiplier: 1 },
            },
            {
                name: "Vervaldatum",
                field: DUE_DATE_FIELD,
                customRender: (row) => (
                    <span
                        className={daysUntilDueToColorClass(row.days_until_due)}
                    >
                        {formatDueDateLabel(row.normalized_due_date, row.days_until_due)}
                    </span>
                ),
                style: { widthMultiplier: 1.5 },
            },
            {
                name: "Relatie",
                field: "relation",
                customRender: (row) => <span>{row.relation}</span>,
                style: { widthMultiplier: 1.5 },
            },
            ...(onReportDocumentIncorrect
                ? [
                      {
                          name: "",
                          field: "id",
                          disableSorting: true,
                          customRender: (row: OutstandingBillRow) => (
                              <button
                                  type="button"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      onReportDocumentIncorrect(row);
                                  }}
                                  className="text-gray-400 hover:text-gray-700 hover:underline text-sm"
                              >
                                  {REPORT_INCORRECT_LABEL}
                              </button>
                          ),
                          style: { widthMultiplier: 1 },
                      } as const,
                  ]
                : []),
        ],
        onEmptyRender: () => <span className="text-gray-500">{emptyMessage}</span>,
        pagination: {
            inMemory: true,
            paginationState: tablePaginationState,
            totalPages: Math.ceil(bills.length / PAGE_SIZE),
            defaultPageSize: PAGE_SIZE,
        },
        sorting: {
            sortingState: tableSortingState,
            inMemory: true,
        },
        search: {
            searchFn: (row: OutstandingBillRow, searchState: SearchState) => {
                const query = searchState.currentQuery.toLowerCase();
                return SEARCHABLE_FIELDS.some((readField) =>
                    (readField(row) ?? EMPTY_FIELD_VALUE).toLowerCase().includes(query)
                );
            },
        },
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: false,
            allowFullRowClick: true,
        },
        onRowClick,
    };
    const TableComponent = createTable<OutstandingBillRow>(bills, tableSpec);
    return <>{TableComponent()}</>;
}
